import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUp, Loader2 } from "lucide-react";
import { sessionId } from "../lib/session";

const API = process.env.REACT_APP_BACKEND_URL;
const STORE = "lint_chat_v2";
const MAX = 2;

function loadSaved() {
  try {
    return JSON.parse(localStorage.getItem(STORE)) || [];
  } catch {
    return [];
  }
}

async function streamAnswer(question, onDelta) {
  const res = await fetch(`${API}/api/lint/ask`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId(), question }),
  });
  if (!res.ok) throw new Error((await res.json()).error || "Something went wrong.");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop();
    for (const ev of events) {
      if (!ev.startsWith("data: ")) continue;
      const data = JSON.parse(ev.slice(6));
      if (data.error) throw new Error(data.error);
      if (data.delta) onDelta(data.delta);
    }
  }
}

export function LintChat() {
  const [turns, setTurns] = useState(loadSaved);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [maxed, setMaxed] = useState(loadSaved().length >= MAX);
  const left = MAX - turns.length;
  const threadRef = useRef(null);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  async function ask(e) {
    e.preventDefault();
    const q = question.trim();
    if (!q || loading || maxed) return;
    setLoading(true);
    setError("");
    setQuestion("");
    const base = [...turns, { q, a: "" }];
    setTurns(base);
    let full = "";
    try {
      await streamAnswer(q, (d) => {
        full += d;
        setTurns(base.map((t, i) => (i === base.length - 1 ? { q, a: full } : t)));
      });
      const final = base.map((t, i) => (i === base.length - 1 ? { q, a: full } : t));
      localStorage.setItem(STORE, JSON.stringify(final));
      if (final.length >= MAX) setMaxed(true);
    } catch (err) {
      setTurns(turns);
      setQuestion(q);
      setError(err.message);
      if (err.message.includes("two questions")) setMaxed(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-3xl border border-border bg-surface-elevated/90 p-4 shadow-[0_24px_60px_-40px_rgba(31,58,44,0.5)] backdrop-blur sm:p-5" data-testid="lint-chat">
      <div ref={threadRef} className="max-h-80 space-y-4 overflow-y-auto" data-testid="lint-chat-thread">
        {turns.map((t, i) => <Turn key={i} t={t} i={i} />)}
      </div>
      {!maxed && (
        <form onSubmit={ask} className={`relative ${turns.length ? "mt-4" : ""}`} data-testid="lint-chat-form">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading}
            maxLength={600}
            rows={2}
            placeholder={turns.length ? "Ask Lint a follow-up…" : "Tell Lint your idea, e.g. a marketplace for local bakers"}
            className="w-full resize-none rounded-2xl border border-border bg-white px-4 py-3 pr-14 text-sm text-navy outline-none ring-sage placeholder:text-ink-muted/70 focus:ring-2 disabled:opacity-70"
            data-testid="lint-chat-input"
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            aria-label="Ask Lint"
            className="absolute bottom-3.5 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-brand text-cream transition-colors hover:bg-navy disabled:opacity-40"
            data-testid="lint-chat-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} />}
          </button>
          <p className="mt-2 text-xs text-ink-muted" data-testid="lint-chat-remaining">
            {left} free question{left === 1 ? "" : "s"} left
          </p>
        </form>
      )}
      {error && <p className="mt-3 text-sm text-critical" role="alert" data-testid="lint-chat-error">{error}</p>}
      {maxed && <ChatCta />}
    </div>
  );
}

function Turn({ t, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
      <p className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-md bg-brand px-3.5 py-2 text-sm text-cream" data-testid={`lint-chat-question-${i}`}>
        {t.q}
      </p>
      <div className="flex items-start gap-2.5">
        <img src="/lint_deep_dive.webp" alt="Lint" className="h-10 w-10 shrink-0 object-contain" />
        <p className="rounded-2xl rounded-tl-md border border-border bg-cream px-3.5 py-2.5 text-sm leading-relaxed text-navy" data-testid={`lint-chat-answer-${i}`}>
          {t.a || <span className="text-ink-muted">Lint is thinking…</span>}
        </p>
      </div>
    </motion.div>
  );
}

function ChatCta() {
  return (
    <div className="mt-4 flex flex-col items-start gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm text-ink-muted">That's your two free questions. Want Lint on the whole product?</p>
      <a
        href="#waitlist"
        className="shrink-0 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-brand"
        data-testid="lint-chat-book-demo"
      >
        Book a demo
      </a>
    </div>
  );
}
